import * as React from "react";
import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import "./LogTable.css";
import {
  Alert,
  Avatar,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  TablePagination,
  TableSortLabel,
  Typography,
} from "@mui/material";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import LogTableAdditionalContent from "./LogTableAdditionalContent";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import PendingOutlinedIcon from "@mui/icons-material/PendingOutlined";
import AutorenewOutlinedIcon from "@mui/icons-material/AutorenewOutlined";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import { useSelector } from "react-redux";
import { selectUserData } from "../../../../../features/userSlice";
import HourglassBottomIcon from "@mui/icons-material/HourglassBottom";
import { CheckPermissions } from "../../../../../logic/Admin/Permissions/CheckPermission/CheckPermissions";
import { formatDate } from '../../../../../utils/helpers';
interface Props {
  tableHeadings: { [key: string]: string };
  logs: any;
  selectedStatus: any;
  selectedMethod: any;
  error: any;
  setRefresh: any;
  filteredLogsByLastRun: any;
  setSelectedIds: any;
  selectedIds: any;
  selectedName: any;
  setSelectedName: any;
  page: any;
  setPage: any;
  setRowsPerPage: any;
  rowsPerPage: any;
  setRowID: any;
  totalRecords: any;
  setIsNextButton: any;
  setIsPrevButton: any;
  isClearFilter: any;
  filteredLogs: any;
  setFilteredLogs: any;
  rowID: any;
}

export default function LogTable({
  tableHeadings,
  logs,
  error,
  setRefresh,
  filteredLogsByLastRun,
  setSelectedIds,
  page,
  setPage,
  setRowsPerPage,
  rowsPerPage,
  setRowID,
  totalRecords,
  setIsNextButton,
  setIsPrevButton,
  isClearFilter,
  filteredLogs,
  setFilteredLogs,
  rowID,
}: Props) {
  const [openRow, setOpenRow] = React.useState<number | null>(null);
  const headCells = Object.entries(tableHeadings);
  const [open, setOpen] = React.useState(true);
  const [runId, setRunId] = React.useState<number[]>([]);
  const response = useSelector(selectUserData);
  const [order, setOrder] = React.useState<"asc" | "desc">("asc");
  const [orderBy, setOrderBy] = React.useState<string>("");
  // const [filteredLogs, setFilteredLogs] = React.useState<any[]>([]);

  const hasViewProcessLogPermission = CheckPermissions(
    response?.data?.products?.admin,
    "processes",
    "view_process_log"
  );

  const navigate = useNavigate();

  React.useEffect(() => {
    setFilteredLogs(
      isClearFilter
        ? logs.filter((log: any) => {
            const lastRunMatches =
              filteredLogsByLastRun && filteredLogsByLastRun.length !== 0
                ? filteredLogsByLastRun.some(
                    (filteredLog: any) => filteredLog.run_id === log.run_id
                  )
                : true; // Check if the log is part of the latest run

            return lastRunMatches;
          })
        : logs
    );
    // eslint-disable-next-line
  }, [logs, isClearFilter, filteredLogsByLastRun]);

  const handleChangePage = (_: unknown, newPage: any) => {
    if (newPage > page) {
      setIsNextButton(true);
      setIsPrevButton(false);
    } else if (newPage < page) {
      setIsNextButton(false);
      setIsPrevButton(true);
    }
    setPage(newPage); // Update the page state
    const newSearchParams = new URLSearchParams(window.location.search);
    newSearchParams.set("page", newPage.toString());

    if (newPage > 0) {
      newSearchParams.set("run_id", rowID);
    } else {
      newSearchParams.delete("run_id");
    }

    window.history.replaceState(
      {},
      "",
      `${window.location.pathname}?${newSearchParams}`
    );
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleRowClick = (index: number) => {
    setOpenRow(openRow === index ? null : index);
  };

  const handleRetry = () => {
    setRefresh(true);
  };

  const handleClose = () => {
    setOpen(false);
    navigate(`/`);
  };

  const handleRequestSort = (property: string) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const comparator = (a: any, b: any) => {
    const aValue = a[orderBy];
    const bValue = b[orderBy];

    // Check if the values are dates and convert them to Date objects for comparison
    if (orderBy === "start" || orderBy === "end") {
      const dateA = new Date(aValue);
      const dateB = new Date(bValue);

      if (dateA < dateB) {
        return order === "asc" ? -1 : 1;
      }
      if (dateA > dateB) {
        return order === "asc" ? 1 : -1;
      }
      return 0;
    }

    // Default comparison for other columns (strings, numbers, etc.)
    if (aValue < bValue) {
      return order === "asc" ? -1 : 1;
    }
    if (aValue > bValue) {
      return order === "asc" ? 1 : -1;
    }
    return 0;
  };

  // Compute sortedLogs directly
  const sortedLogs = isClearFilter ? logs : filteredLogs.sort(comparator);

  const handleCheckboxChange = (
    process_id: number,
    run_id: any,
    isChecked: boolean
  ) => {
    if (isChecked) {
      // Add the run_id to the selected array
      setSelectedIds((prevIds: any) => [...prevIds, process_id]);
      setRunId((prevIds: any) => [...prevIds, run_id]);
    } else {
      // Remove the run_id from the selected array
      setSelectedIds((prevIds: any) =>
        prevIds.filter((id: any) => id !== process_id)
      );
      setRunId((prevIds: any) => prevIds.filter((id: any) => id !== run_id));
    }
  };
  React.useEffect(() => {
    const lastRowId = logs?.length ? logs[logs.length - 1]?.run_id : null;
    setRowID(lastRowId);
  }, [logs, setRowID]);

  return (
    <>
      {error && Object.values(error).length >= 1 ? (
        <Dialog
          open={open}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title">{error}</DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              Invalid Page Structure or Page Group Missing
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} autoFocus color="secondary">
              Back
            </Button>
            <Button onClick={handleRetry} autoFocus color="secondary">
              Retry
            </Button>
          </DialogActions>
        </Dialog>
      ) : (
        <TableContainer
          className="LogTableWrap"
          component={Paper}
          sx={{ maxHeight: 800 }}
        >
          <Table aria-label="collapsible table" size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {headCells.map(([key, value], index) => (
                  <TableCell key={index} align="left">
                    <TableSortLabel
                      active={orderBy === key}
                      direction={orderBy === key ? order : "asc"}
                      onClick={() => handleRequestSort(key)}
                    >
                      {value && typeof value === "string" ? value : ""}
                    </TableSortLabel>
                  </TableCell>
                ))}
                {/* <TableCell align="center">Actions</TableCell> */}
              </TableRow>
            </TableHead>
            <TableBody>
              {logs?.length > 0 && logs ? (
                sortedLogs
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row: any, index: number) => (
                    <React.Fragment key={row.cron_id}>
                      <TableRow sx={{ "& > *": { borderBottom: "unset" } }}>
                        {headCells.map(([key], idx) => (
                          <TableCell
                            key={idx}
                            align="left"
                            sx={{
                              backgroundColor: runId.includes(row?.run_id)
                                ? "#F7EEF9"
                                : "transparent",
                            }}
                          >
                            {key === "run_id" ? (
                              <>
                                <Checkbox
                                  size="small"
                                  color="secondary"
                                  checked={runId.includes(row?.run_id)}
                                  onChange={(e) =>
                                    handleCheckboxChange(
                                      row.process_id,
                                      row?.run_id,
                                      e.target.checked
                                    )
                                  }
                                />
                                {row[key]}
                              </>
                            ) : key === "info" ? (
                              <IconButton
                                aria-label="expand row"
                                size="small"
                                color="secondary"
                                onClick={() => handleRowClick(index)}
                                disabled
                              >
                                {openRow === index ? (
                                  <KeyboardArrowUpIcon />
                                ) : (
                                  <KeyboardArrowDownIcon />
                                )}
                              </IconButton>
                            ) : key === "status" ? (
                              <Alert
                                icon={
                                  row?.status === "SUCCESS" ? (
                                    <CheckCircleOutlineIcon
                                      sx={{
                                        color: "#1E4620",
                                        width: "18px",
                                        height: "18px",
                                      }}
                                    />
                                  ) : row?.status === "RUNNING" ? (
                                    <AutorenewOutlinedIcon
                                      sx={{
                                        color: "#7B1FA2",
                                        width: "18px",
                                        height: "18px",
                                      }}
                                    />
                                  ) : row?.status === "WARNING" ? (
                                    <HighlightOffIcon
                                      sx={{
                                        color: "#ED6C02",
                                        width: "18px",
                                        height: "18px",
                                      }}
                                    />
                                  ) : row?.status === "PENDING" ? (
                                    <PendingOutlinedIcon
                                      sx={{
                                        color: "#014361",
                                        width: "18px",
                                        height: "18px",
                                      }}
                                    />
                                  ) : row?.status === "QUEUED" ? (
                                    <HourglassBottomIcon
                                      sx={{
                                        color: "#014361",
                                        width: "18px",
                                        height: "18px",
                                      }}
                                    />
                                  ) : (
                                    <CancelOutlinedIcon
                                      sx={{
                                        color: "#5F2120",
                                        width: "18px",
                                        height: "18px",
                                      }}
                                    />
                                  )
                                }
                                sx={{
                                  height: "30px",
                                  display: "flex",
                                  alignItems: "center",
                                  // width: "100px",
                                  padding: "6px",
                                  border: "1px solid",
                                  color:
                                    row?.status === "SUCCESS"
                                      ? "#1E4620"
                                      : row?.status === "RUNNING"
                                      ? "#7B1FA2"
                                      : row?.status === "WARNING"
                                      ? "#ED6C02"
                                      : row?.status === "PENDING"
                                      ? "#014361"
                                      : row?.status === "CANCELLED"
                                      ? "#5F2120"
                                      : row?.status === "FAILURE"
                                      ? "#5F2120"
                                      : "defaultBackgroundColor",
                                  borderColor:
                                    row?.status === "SUCCESS"
                                      ? "#2E7D3280"
                                      : row?.status === "RUNNING"
                                      ? "#9C27B01F"
                                      : row?.status === "WARNING"
                                      ? "#ED6C0280"
                                      : row?.status === "PENDING"
                                      ? "#2196F380"
                                      : row?.status === "CANCELLED"
                                      ? "#D32F2F80"
                                      : row?.status === "FAILURE"
                                      ? "#D32F2F80"
                                      : "defaultBackgroundColor",
                                  backgroundColor:
                                    row?.status === "SUCCESS"
                                      ? "#EDF7ED"
                                      : row?.status === "RUNNING"
                                      ? "#F3E5F5"
                                      : row?.status === "WARNING"
                                      ? "#FFF4E5"
                                      : row?.status === "PENDING"
                                      ? "#E5F6FD"
                                      : row?.status === "CANCELLED"
                                      ? "#FDEDED"
                                      : row?.status === "FAILURE"
                                      ? "#FDEDED"
                                      : "defaultBackgroundColor",
                                }}
                                variant="outlined"
                              >
                                {row?.status === "SUCCESS"
                                  ? "Success"
                                  : row?.status === "RUNNING"
                                  ? "Running"
                                  : row?.status === "WARNING"
                                  ? "Warning"
                                  : row?.status === "PENDING"
                                  ? "Pending"
                                  : row?.status === "CANCELLED"
                                  ? "Cancelled"
                                  : row?.status === "QUEUED"
                                  ? "Queued"
                                  : "Failure"}
                              </Alert>
                            ) : key === "response" ? (
                              <>
                                {hasViewProcessLogPermission ? (
                                  <RouterLink
                                    to={`etl-process-log-Response?response_run_id=${row?.run_id}`}
                                    state={{
                                      runId: row?.run_id,
                                    }}
                                    className="custom-link"
                                    data-tracker="230"
                                  >
                                    View Log
                                  </RouterLink>
                                ) : (
                                  <Typography color={"#D3D3D3"}>
                                    View Log
                                  </Typography>
                                )}
                              </>
                            ) : key === "run_by" ? (
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 5,
                                }}
                              >
                                <Avatar sx={{ width: 24, height: 24 }}>
                                  {row[key] && row[key].charAt(0)}
                                </Avatar>
                                {row[key] ? row[key] : "N/A"}
                              </div>
                            ) : key === "start_time" ? (
                              <>
                                {formatDate(row?.start_time)}
                              </>
                            ) : key === "end_time" ? (
                              <>
                                {formatDate(row?.end_time)}
                              </>
                            ) : key === "record_created" ? (
                              <>{row?.record_created}</>
                            ) : key === "record_updated" ? (
                              <>{row?.record_updated}</>
                            ) : key === "record_total" ? (
                              <>{row?.record_total}</>
                            ) : key === "method" ? (
                              <>{row?.run_method ? row?.run_method : "N/A"}</>
                            ) : key === "process_name" ? (
                              <>
                                {row?.process_name ? row?.process_name : "N/A"}
                              </>
                            ) : row[key] ? (
                              row[key]
                            ) : (
                              "N/A"
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                      <TableRow>
                        <TableCell
                          style={{
                            paddingBottom: 0,
                            paddingTop: 0,
                            border: "none",
                          }}
                          colSpan={Object.keys(tableHeadings).length + 1}
                        >
                          <Collapse
                            in={openRow === index}
                            timeout="auto"
                            unmountOnExit
                          >
                            <Box sx={{ padding: "20px 0px" }}>
                              <LogTableAdditionalContent rowData={row} />
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={headCells.length} // Adjust this number to match the number of columns in your table
                    align="center"
                    sx={{
                      margin: "15px",
                      textAlign: "center",
                      padding: "10px",
                      fontSize: "25px",
                    }}
                  >
                    No Data to Display
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <Stack
            spacing={1}
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <TablePagination
              rowsPerPageOptions={[10, 25, 100]}
              component="div"
              count={totalRecords}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Stack>
        </TableContainer>
      )}
    </>
  );
}
